/**
 * Concluir tarefa com um clique, e poder desfazer (H10).
 *
 * ── Por que desfazer em vez de confirmar ──────────────────────────────────
 *
 * A Técnica da task: *"confirmação para concluir tarefa é atrito desnecessário;
 * desfazer resolve melhor"*. Quem conclui vinte tarefas por dia clica em vinte
 * confirmações — e passa a clicar sem ler, o que anula a proteção e mantém o
 * atrito.
 *
 * ── Mas não existe rota para reabrir tarefa ───────────────────────────────
 *
 * O `/v1` tem `POST /tasks/{id}/complete` e nada que desfaça. Então o desfazer
 * daqui é real por outro caminho: o comando **não sai** durante a janela. A
 * tarefa aparece concluída na tela, e o `POST` é enviado alguns segundos depois.
 * Desfazer dentro da janela cancela algo que ainda não aconteceu.
 *
 * É honesto e é o que o padrão do Gmail faz. A alternativa — enviar e depois
 * tentar reverter — dependeria de uma rota que não existe, e prometeria um
 * desfazer que às vezes falharia.
 *
 * ── O que a janela cobra ──────────────────────────────────────────────────
 *
 * Sair da tela dentro dela deixaria a conclusão sem sair. Por isso quem usa
 * este módulo esvazia a fila ao desmontar: fechar a tela vale como confirmar,
 * que é o comportamento que a pessoa espera de algo que ela já viu marcado.
 */

export const JANELA_DE_DESFAZER_MS = 6_000;

export interface ConclusaoPendente {
  readonly id: string;
  readonly titulo: string;
  /** Quando o clique aconteceu. O envio é este instante mais a janela. */
  readonly clicadaEm: number;
}

/**
 * Agenda a conclusão.
 *
 * Clicar de novo na mesma tarefa NÃO reinicia a janela: reiniciar faria o
 * clique repetido — que é comum quando a tela não responde na hora — empurrar o
 * envio indefinidamente, e a tarefa nunca concluiria.
 */
export function agendar(
  pendentes: readonly ConclusaoPendente[],
  id: string,
  titulo: string,
  agora: number,
): readonly ConclusaoPendente[] {
  if (pendentes.some((p) => p.id === id)) return pendentes;
  return [...pendentes, { id, titulo, clicadaEm: agora }];
}

export function desfazer(
  pendentes: readonly ConclusaoPendente[],
  id: string,
): readonly ConclusaoPendente[] {
  return pendentes.filter((p) => p.id !== id);
}

/**
 * O que já passou da janela e precisa ser enviado.
 *
 * Devolve as duas listas porque quem chama precisa das duas: envia uma e guarda
 * a outra. Calcular só as prontas obrigaria o chamador a refazer o filtro
 * inverso, com o mesmo `agora` — e um `agora` diferente entre os dois cálculos
 * é como um item se perde.
 */
export function separarProntas(
  pendentes: readonly ConclusaoPendente[],
  agora: number,
): {
  readonly enviar: readonly ConclusaoPendente[];
  readonly esperando: readonly ConclusaoPendente[];
} {
  const enviar: ConclusaoPendente[] = [];
  const esperando: ConclusaoPendente[] = [];

  for (const pendente of pendentes) {
    if (agora - pendente.clicadaEm >= JANELA_DE_DESFAZER_MS) enviar.push(pendente);
    else esperando.push(pendente);
  }

  return { enviar, esperando };
}

/** Quanto falta da janela, para a contagem no aviso de desfazer. */
export function segundosRestantes(pendente: ConclusaoPendente, agora: number): number {
  const restante = JANELA_DE_DESFAZER_MS - (agora - pendente.clicadaEm);
  return Math.max(0, Math.ceil(restante / 1000));
}

/**
 * A tarefa aparece concluída enquanto a conclusão está pendente?
 *
 * Sim, e é o ponto do desfazer: a tela responde ao clique na hora. O que não
 * acontece na hora é o comando — e a diferença fica visível pelo próprio aviso
 * de desfazer, que só existe enquanto dá tempo.
 */
export function pareceConcluida(
  tarefa: { readonly id: string | null; readonly status: string },
  pendentes: readonly ConclusaoPendente[],
): boolean {
  if (tarefa.status === 'DONE') return true;
  return tarefa.id !== null && pendentes.some((p) => p.id === tarefa.id);
}
